import lodashHas from "lodash/has";
import lodashGet from "lodash/get";
import lodashIsEmpty from "lodash/isEmpty";
import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";

import { MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import store from "@/store";
import { ASSET_ICONS } from "@/common/constants/assetManagement";
import {
  ENDPOINT_ASSETS,
  ENDPOINT_ASSETS_CONFIG,
} from "@/common/constants/api";
import { httpGet, httpPost, httpPut, httpDelete } from "@/services/api.service";
import {
  getFormatted,
  getTodayStartEndDate,
  daysAgoUtc,
} from "@/common/date.utilities";
import {
  getCityDisplayNameByCode,
  getCountryDisplayNameByCode,
  getCountries,
} from "@/common/maps/utilities";
import { getColorByStabilityScore } from "@/services/grids/grids.service";

export { _getAssetById as getAssetById };
export { _getAssets as getAssets };
export { _loadMapIcons as loadMapIcons };
export { _addAsset as addAsset };
export { _editAsset as editAsset };
export { _deleteAsset as deleteAsset };
export { _getAssetTypes as getAssetTypes };
export { _addAssetType as addAssetType };
export { _editAssetType as editAssetType };
export { _deleteAssetType as deleteAssetType };
export { _getCustomFields as getCustomFields };
export { _addCustomField as addCustomField };
export { _editCustomField as editCustomField };
export { _deleteCustomField as deleteCustomField };
export { _searchWithAssetRadius as searchWithAssetRadius };
export { _getAssetsConfig as getAssetsConfig };

let COUNTRIES = null;
let assetsConfig = null;

const _getAssetById = async (id) => {
  try {
    if (!id) {
      console.error("Unable to get asset");
      return false;
    }
    let res = await httpGet({
      url: `${ENDPOINT_ASSETS}/${id}`,
      noCache: true,
    });
    res = JSON.parse(res);
    if (lodashHas(res, "data.asset")) {
      _formatFeature(res.data.asset);
      return res.data.asset;
    }
    return true;
  } catch (error) {
    console.error("Unable to get asset: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

// Assets
const _getAssets = async (params) => {
  try {
    let res = await httpGet({
      url: _buildQuery(params),
      noCache: true,
    });
    res = JSON.parse(res);
    if (lodashHas(res, "data.assets")) {
      await _formatFeatures(res.data.assets, params);
      return res.data;
    }
  } catch (error) {
    console.error("Unable to get assets by user group", error);
    return false;
  }
};

const _buildQuery = (params) => {
  let baseUrl = ENDPOINT_ASSETS;
  if (params) {
    const {
      filterCountries,
      filterAssetTypeIds,
      filterAssetIds,
      filterTitle,
      filterRatings,
      filterAlertId,
      filterDateRange,
      filterDateRangeCompare,
      sortField,
      sortDirection,
      page,
      itemsPerPage,
      fields,
      eventsSeverity,
    } = params?.options;
    if (!!filterCountries && !lodashIsEmpty(filterCountries)) {
      baseUrl += _appendQuery(baseUrl, `countries=${filterCountries}`);
    }
    if (!!filterAssetTypeIds && !lodashIsEmpty(filterAssetTypeIds)) {
      baseUrl += _appendQuery(baseUrl, `assetTypeIds=${filterAssetTypeIds}`);
    }
    if (!!filterAssetIds && !lodashIsEmpty(filterAssetIds)) {
      baseUrl += _appendQuery(baseUrl, `assetIds=${filterAssetIds}`);
    }
    if (!!filterTitle && !lodashIsEmpty(filterTitle)) {
      baseUrl += _appendQuery(baseUrl, `title=${filterTitle}`);
    }
    if (!!filterRatings && !lodashIsEmpty(filterRatings)) {
      baseUrl += encodeURI(
        _appendQuery(
          baseUrl,
          `riskRatingSeverityFilter=${JSON.stringify(filterRatings)}`
        )
      );
    }
    if (!!filterAlertId && !lodashIsEmpty(filterAlertId)) {
      baseUrl += _appendQuery(baseUrl, `alertId=${filterAlertId}`);
    }
    if (!!filterDateRange && filterDateRange.start && filterDateRange.end) {
      baseUrl += _appendQuery(
        baseUrl,
        `eventsStartDate=${filterDateRange.start}`
      );
      baseUrl += _appendQuery(baseUrl, `eventsEndDate=${filterDateRange.end}`);
    }
    if (filterDateRangeCompare?.start && filterDateRangeCompare?.end) {
      baseUrl += _appendQuery(
        baseUrl,
        `eventsCompStartDate=${filterDateRangeCompare.start}`
      );
      baseUrl += _appendQuery(
        baseUrl,
        `eventsCompEndDate=${filterDateRangeCompare.end}`
      );
    }
    if (!!sortDirection && !lodashIsEmpty(sortDirection)) {
      baseUrl += _appendQuery(
        baseUrl,
        `sortDirection=${sortDirection.toUpperCase()}`
      );
    }
    if (!!sortField && !lodashIsEmpty(sortField)) {
      baseUrl += _appendQuery(baseUrl, `sortField=${sortField}`);
    }
    if (!!page) {
      baseUrl += _appendQuery(baseUrl, `page=${page}`);
    }
    if (!!itemsPerPage) {
      baseUrl += _appendQuery(baseUrl, `pageSize=${itemsPerPage}`);
    }
    if (!!fields && !lodashIsEmpty(fields)) {
      baseUrl += _appendQuery(baseUrl, `fields=${JSON.stringify(fields)}`);
    }
    if (!!eventsSeverity) {
      baseUrl += _appendQuery(baseUrl, `eventsSeverity=${eventsSeverity}`);
    }
  }
  return baseUrl;
};

const _appendQuery = (query, addition) => {
  return (query.includes("?") ? "&" : "?") + addition;
};

const _addAsset = async (params) => {
  try {
    const formattedData = _formatAssetForApi(params);
    await httpPost({
      url: ENDPOINT_ASSETS,
      data: formattedData,
    });
    return true;
  } catch (error) {
    console.error("Unable to save an asset", error);
    return false;
  }
};

const _editAsset = async (params) => {
  try {
    const formattedData = _formatAssetForApi(params);
    await httpPut({
      url: `${ENDPOINT_ASSETS}/${params.id}`,
      data: formattedData,
    });
    return true;
  } catch (error) {
    console.error("Unable to edit an asset", error);
    return false;
  }
};

const _deleteAsset = async (id) => {
  try {
    if (!id) {
      console.error("Unable to delete asset");
      return false;
    }
    await httpDelete({
      url: `${ENDPOINT_ASSETS}/${id}`,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete asset: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _formatAssetForApi = (asset) => {
  return {
    title: asset.title,
    assetTypeId: asset.assetTypeId,
    latitude: parseFloat(asset.latitude),
    longitude: parseFloat(asset.longitude),
    fullAddress: asset.fullAddress,
    countryAlpha2Code: asset.country.code,
    customFields: asset.customFields,
    monitorRadius: parseFloat(asset.monitorRadius),
    expirationDate: asset.expirationDate,
    city: asset.city,
  };
};

// Asset Types
const _getAssetTypes = async () => {
  try {
    let res = await httpGet({
      url: ENDPOINT_ASSETS + "/asset-types",
      noCache: true,
    });
    const { data } = JSON.parse(res);
    return data.assetTypes;
  } catch (error) {
    console.error("Unable to get asset types by user group", error);
    return false;
  }
};
const _addAssetType = async (params) => {
  try {
    const cleanParams = lodashCloneDeep(params);
    if (lodashHas(cleanParams, "isEditing")) {
      delete cleanParams.isEditing;
    }
    await httpPost({
      url: ENDPOINT_ASSETS + "/asset-types",
      data: cleanParams,
    });
    return true;
  } catch (error) {
    console.error("Unable to save an asset type", error);
    return false;
  }
};

const _editAssetType = async (params) => {
  try {
    const cleanParams = lodashCloneDeep(params);
    if (lodashHas(cleanParams, "isEditing")) {
      delete cleanParams.isEditing;
    }
    await httpPut({
      url: `${ENDPOINT_ASSETS}/asset-types/${cleanParams.id}`,
      data: cleanParams,
    });
    return true;
  } catch (error) {
    console.error("Unable to edit an asset type", error);
    return false;
  }
};

const _deleteAssetType = async (id) => {
  try {
    await httpDelete({
      url: `${ENDPOINT_ASSETS}/asset-types/${id}`,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete an asset type", error);
    return false;
  }
};

// Custom Fields
const _getCustomFields = async () => {
  try {
    let res = await httpGet({
      url: ENDPOINT_ASSETS + "/custom-fields",
      noCache: true,
    });
    const { data } = JSON.parse(res);
    if (data?.customFields) {
      data.customFields.forEach((field) => {
        // This is to fix an issue where the asset data table doesn't show data in
        // a column if the header (title) has a period in it. So we're removing all
        // periods in custom field titles. But also fixing the adding of custom fields
        // so users can't an a custom field with a period in it. This is to fix data
        // that might already be in there.
        field.title = field.title.replace(/\./g, "");
      });
    }
    return data?.customFields;
  } catch (error) {
    console.error("Unable to get custom fields by user group", error);
    return false;
  }
};
const _addCustomField = async (params) => {
  try {
    const cleanParams = lodashCloneDeep(params);
    if (lodashHas(cleanParams, "isEditing")) {
      delete cleanParams.isEditing;
    }
    await httpPost({
      url: ENDPOINT_ASSETS + "/custom-fields",
      data: cleanParams,
    });
    return true;
  } catch (error) {
    console.error("Unable to save a custom field", error);
    return false;
  }
};

const _editCustomField = async (params) => {
  try {
    const cleanParams = lodashCloneDeep(params);
    if (lodashHas(cleanParams, "isEditing")) {
      delete cleanParams.isEditing;
    }
    await httpPut({
      url: `${ENDPOINT_ASSETS}/custom-fields/${cleanParams.id}`,
      data: cleanParams,
    });
    return true;
  } catch (error) {
    console.error("Unable to edit a custom field", error);
    return false;
  }
};

const _deleteCustomField = async (id) => {
  try {
    await httpDelete({
      url: `${ENDPOINT_ASSETS}/custom-fields/${id}`,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete a custom field", error);
    return false;
  }
};

const getCountryLookup = async () => {
  if (!COUNTRIES) {
    const countries = (await getCountries()) ?? [];
    COUNTRIES = countries.reduce((acc, country) => {
      acc[country.isoAlpha2Code] = {
        name: country.name[0].text,
        isoAlpha2Code: country.isoAlpha2Code,
        countryAlpha2Code: country.isoAlpha2Code,
        shortDisplayName: country.isoAlpha3Code.toUpperCase(),
        code: country.isoAlpha3Code.toUpperCase(),
        flagUrl: country.flagUrl,
      };
      return acc;
    }, {});
  }
  return COUNTRIES;
};

// Utilities
const _formatFeatures = async (features, params) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    await _formatFeature(feature, params);
  }
};

const _formatFeature = async (feature, params) => {
  if (feature) {
    feature.formatted = {};
    let countryLookup = await getCountryLookup();
    if (
      feature.country?.countryAlpha2Code &&
      countryLookup[feature.country.countryAlpha2Code]
    ) {
      feature.country = countryLookup[feature.country.countryAlpha2Code];
    } else if (feature.country?.countryAlpha2Code) {
      // Missing countries in lookup like "Hong Kong"
      feature.country.isoAlpha2Code = feature.country.countryAlpha2Code;
      feature.country.shortDisplayName = feature.country.countryAlpha2Code.toUpperCase();
    }

    feature.formatted.expirationDate = feature.expirationDate
      ? getFormatted(feature.expirationDate)
      : null;

    feature.formatted.created = feature.creationTimestamp
      ? getFormatted(feature.creationTimestamp)
      : null;
    feature.formatted.updated = feature.lastUpdatedTime
      ? getFormatted(feature.lastUpdatedTime)
      : null;

    feature.formatted.monitorRadius =
      typeof feature.monitorRadius === "string"
        ? parseFloat(feature.monitorRadius)
        : feature.monitorRadius;

    feature.hotspotCount = feature.hotspotCount ?? "No content";

    if (!!feature.pulseLocationCode) {
      feature.pulseLocationName =
        (await _getPulseLocationDisplayName(feature.pulseLocationCode)) ??
        "Unspecified";
    }
    if (!!feature.pulseScore) {
      feature.pulseColor = getColorByStabilityScore(feature.pulseScore);
    }

    // This is to fix an issue where the asset data table doesn't show data in
    // a column if the header (title) has a period in it. So we're removing all
    // periods in custom field titles. But also fixing the adding of custom fields
    // so users can't an a custom field with a period in it. This is to fix data
    // that might already be in there.
    if (feature.customFields) {
      Object.keys(feature.customFields).forEach((key) => {
        if (key.includes(".")) {
          const newKey = key.replace(/\./g, "");
          feature.customFields[newKey] = feature.customFields[key];
          delete feature.customFields[key];
        }
      });
    }
  }
};
const _getPulseLocationDisplayName = async (pulseLocationCode) => {
  let cityName = await getCityDisplayNameByCode(pulseLocationCode);
  if (cityName) {
    return cityName;
  }
  let countryName = await getCountryDisplayNameByCode(pulseLocationCode);
  if (countryName) {
    return countryName;
  }
  return pulseLocationCode;
};
const _loadMapIcon = (map, id, src) => {
  if (map.hasImage(id)) {
    map.removeImage(id);
  }
  map.loadImage(src, (err, image) => {
    if (err) {
      console.error(err);
    } else {
      map.addImage(id, image, { sdf: true });
    }
  });
};

const _loadMapIcons = (map) => {
  ASSET_ICONS.forEach((icon) => {
    _loadMapIcon(
      map,
      `${icon.value}`,
      require(`@/assets/images/icons/assetManagement/${icon.fileName}.png`)
    );
  });
};

const _searchWithAssetRadius = async (asset) => {
  const preferences = lodashCloneDeep(
    store.getters[MODULE_PREFIX_USER + "preferences"]
  );
  preferences.liveSearch = {};

  if (asset?.id && asset?.latitude && asset?.longitude) {
    preferences.liveSearch.assets = [];
    preferences.liveSearch.assets.push(asset.id);
    preferences.liveSearch.boundsType = "assets";
    preferences.liveSearch.placesPoisDistUnits = "km";
    preferences.liveSearch.poisDistUnits = "km";
    if (asset.monitorRadius) {
      preferences.liveSearch.placesRadius = Number(asset.monitorRadius);
      preferences.liveSearch.radius = Number(asset.monitorRadius);
      preferences.liveSearch.pois = `[[${asset.longitude},${
        asset.latitude
      },${Number(asset.monitorRadius)},"${asset.id}"]]`;
    }
  }

  const { severityMin = 1, severityMax = 10 } =
    preferences.assets?.filters?.eventsSeverity === "low"
      ? { severityMin: 1, severityMax: 3 }
      : preferences.assets?.filters?.eventsSeverity === "high"
      ? { severityMin: 4, severityMax: 10 }
      : { severityMin: 1, severityMax: 10 };

  preferences.liveSearch.severityMin = severityMin;
  preferences.liveSearch.severityMax = severityMax;
  preferences.liveSearch.eventType = "verified";
  preferences.liveSearch.verifiedType = "security";
  preferences.liveSearch.sources = "political";

  let start = getTodayStartEndDate(daysAgoUtc(30)).start;
  if (preferences.assets?.filters?.dateRange?.start) {
    start = getTodayStartEndDate(preferences.assets.filters.dateRange.start)
      .start;
  }
  let end = getTodayStartEndDate().end;
  if (preferences.assets?.filters?.dateRange?.end) {
    end = getTodayStartEndDate(preferences.assets.filters.dateRange.end).end;
  }
  preferences.liveSearch.start = start;
  preferences.liveSearch.end = end;
  preferences.liveSearch.time_range = dayjs(start).diff(end, "day");

  preferences.force = true;
  await store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
};

const _getAssetsConfig = async () => {
  if (!assetsConfig) {
    try {
      const url = ENDPOINT_ASSETS_CONFIG;
      const result = await httpGet({ url, noCache: true });
      const { data } = JSON.parse(result);

      if (data?.defaultSystemAssetTypes?.length) {
        assetsConfig = data.defaultSystemAssetTypes;
      }

      return assetsConfig;
    } catch (error) {
      console.error(
        "Issue getting assets config in assets.service.js: ",
        error
      );
      return null;
    }
  }
  return lodashCloneDeep(assetsConfig);
};
